import { Button, Label, Spinner, TextInput } from 'flowbite-react';
import { FormEvent, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isRecruiterSide, WEBSITE_URL } from 'src/config/env.config';
import { sendTrackingEvent } from 'src/utils/gtm';
import { RegisterUserInput, TrackingType, UserExperienceLevelType } from 'types';
import CompanySelection from './CompanySelection';
import { useThemeContext } from 'src/context/ThemeProvider';
import { Dropdown } from 'shared-utils';
import { SingleValue } from 'react-select';
import Select from 'react-select';
import { useMutation } from 'react-query';
import { recordFETracking } from 'src/apis/tracking';

interface Props {
	handleRegister: (e: FormEvent<HTMLFormElement>) => void;
	userInfo: RegisterUserInput;
	setUserInfo: (val: RegisterUserInput) => void;
	isLoading: boolean;
}

const RegisterForm = ({ handleRegister, userInfo, setUserInfo, isLoading }: Props) => {
	const searchParams = new URLSearchParams(window.location.search);
	const params = new URLSearchParams();
	searchParams.forEach((value, key) => {
		params.append(key, value);
	});
	const location = useLocation();
	const { themeMode } = useThemeContext();
	const [experienceInMonths, setExperienceInMonths] = useState(0);
	const [experienceInYears, setExperienceInYears] = useState(0);
	const textInputStyle =
		themeMode === 'dark'
			? { color: 'white', backgroundColor: '#4B5563', border: '1px solid #6B7280' }
			: {
				color: 'black',
				backgroundColor: 'white',
				border: '1px solid #D1D5DB',
			};

	const expRange = {
		year: experienceInYears,
		month: experienceInMonths,
	};
	const recordFETrackingFn = useMutation(['recordFETracking'], recordFETracking, {
		onSuccess() {
			console.log('success tracking');
		},
		onError() {
			console.log('error');
		},
	});

	const experienceOptions = [
		{ label: 'Final Year Student', value: UserExperienceLevelType.FINAL_YEAR_STUDENT, exp: 0 },
		{ label: 'Graduated in 2024', value: UserExperienceLevelType.FRESHER, exp: 0 },
		{ label: '1-2 years experience', value: UserExperienceLevelType.OneToTwoYears, exp: 1 },
		{ label: '2+ years experience', value: UserExperienceLevelType.TwoPlusYears, exp: 2 },
	];

	return (
		<div className="sm:w-full sm:max-w-lg px-4 pt-3 ">
			<form className={'flex flex-col gap-4 shrink-0'} onSubmit={handleRegister}>
				<div className="md:w-[358px]">
					<div className="mb-2 block">
						<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="name" value="Name " />
						<span className="text-red-600">*</span>
					</div>
					<TextInput
						sizing="lg"
						id="name"
						type="text"
						placeholder="Your Name"
						required={true}
						value={userInfo.name}
						onChange={e => setUserInfo({ ...userInfo, name: e.target.value })}
						style={textInputStyle}
					/>
				</div>

				<div className="md:w-[358px]">
					<div className="mb-2 block">
						<Label
							className={'dark:text-[#fff] text-[#111928] font-inter'}
							htmlFor="email"
							value={isRecruiterSide ? 'Work email' : 'Email address'}
						/>
						<span className="text-red-600">*</span>
					</div>
					<TextInput
						sizing="lg"
						id="email"
						type="email"
						pattern={`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`}
						placeholder={isRecruiterSide ? 'name@company.com' : 'name@provider.com'}
						required={true}
						value={userInfo.email}
						onChange={e => setUserInfo({ ...userInfo, email: e.target.value })}
						style={textInputStyle}
					/>
				</div>

				<div className="md:w-[358px]">
					<div className="mb-2 block">
						<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="phone" value="Phone number " />
						{isRecruiterSide ? null : <span className="text-red-600">*</span>}
					</div>
					<TextInput
						sizing="lg"
						id="phone"
						type="tel"
						pattern={`^[0-9]{10}$`}
						placeholder="9876543210"
						required={!isRecruiterSide}
						value={userInfo.phone}
						onChange={e => setUserInfo({ ...userInfo, phone: e.target.value })}
						style={textInputStyle}
					/>
				</div>
				{isRecruiterSide ? (
					<div className="md:w-[358px]">
						<div className="mb-2 block">
							<Label className={'dark:text-[#fff] text-[#111928] font-inter'} htmlFor="company" value="Company" />
							<span className="text-red-600">*</span>
						</div>
						<CompanySelection
							textInputStyle={textInputStyle}
							onChange={v => setUserInfo({ ...userInfo, companyName: v })}
						/>
					</div>
				) : null}

				{isRecruiterSide ? (
					<div className="md:w-[358px]">
						<div className="mb-2 block">
							<Label
								className={'dark:text-[#fff] text-[#111928] font-inter'}
								htmlFor="designation"
								value="Designation"
							/>
						</div>
						<TextInput
							sizing="lg"
							id="designation"
							type="designation"
							placeholder={'Human Resource'}
							required={false}
							value={userInfo.designation}
							onChange={e => setUserInfo({ ...userInfo, designation: e.target.value })}
							style={textInputStyle}
						/>
					</div>
				) : null}

				{isRecruiterSide ? null : (
					<div className="md:w-[358px]">
						<div className="mb-2 block">
							<Label
								className={'dark:text-[#fff] text-[#111928] font-inter'}
								htmlFor="experience"
								value="Experience "
							/>
							<span className="text-red-600">*</span>
						</div>

						<div>
							<Select
								id="experience"
								required
								isClearable
								options={experienceOptions}
								placeholder="Select Experience"
								classNamePrefix="flowbite-select"
								value={experienceOptions?.find(item => item.value === userInfo.experienceLevel)}
								onChange={e =>
									setUserInfo({
										...userInfo,
										experience: e?.exp?.toString() || '0',
										experienceLevel: e?.value as UserExperienceLevelType,
									})
								}
							/>
						</div>
					</div>
				)}

				{/* {isRecruiterSide ? null : (
					<div className="md:w-[358px]">
						<div className="mb-2 block">
							<Label
								className={'dark:text-[#fff] text-[#111928] font-inter'}
								htmlFor="referral"
								value="Referral code "
							/>
							<span className="text-xs italic">(optional)</span>
						</div>
						<TextInput
						size='lg'
							id="referral"
							type="text"
							placeholder="xxxxxx"
							value={userInfo.usedReferralCode}
							onChange={e => setUserInfo({ ...userInfo, usedReferralCode: e.target.value })}
							style={textInputStyle}
						/>
					</div>
				)} */}

				<Button
					color="purplePlaced"
					data-testid="auth-signup"
					disabled={
						isLoading ||
						!userInfo.email ||
						!userInfo.name ||
						(!userInfo.phone && !isRecruiterSide) ||
						(isRecruiterSide ? !userInfo.companyName : !userInfo.experienceLevel || !userInfo.experience)
					}
					type="submit"
					className={'signUp_form_btn'}
				>
					{isLoading ? <Spinner /> : 'Register'}
				</Button>

				<div>
					<div className="flex gap-2 text-base dark:text-[#C7C9D9] text-[#555770]">
						Already a member?
						<Link
							onClick={() => {
								if (isRecruiterSide) {
									sendTrackingEvent({
										event: 'recr_signup_signin',
										eventLabel: location.pathname,
									});
								} else {
									sendTrackingEvent({
										event: 'register_sign_in',
										eventLabel: location.pathname,
									});
								}
							}}
							data-testid="auth-link-signin"
							className="text-[#7777E7]"
							to={`/auth/signin?${params.toString()}`}
						>
							Sign in
						</Link>
					</div>
				</div>

				{isRecruiterSide ? (
					<div>
						<div>
							{/* <div className="h-[1px] bg-[#555770] w-[100%]"></div> */}
							<h1 className="my-[8px] text-center font-[500] text-[16px] leading-[24px] text-[#555770] ">or</h1>
							<div></div>
						</div>

						{isRecruiterSide ? (
							<Button
								color="whiteBlue"
								className={`w-[100%] border border-[#7777E7] bg-[#fff] hover:!bg-[#7777E7] hover:text-[#fff]`}
								onClick={() => {
									// open this in new tab
									sendTrackingEvent({
										event: 'book_demo_sign_up',
										eventLabel: location.pathname,
									});
									if (!recordFETrackingFn.isLoading)
										recordFETrackingFn.mutate({
											type: TrackingType.CLICKED_BOOK_A_DEMO_SIGN_UP,
											createdAt: new Date(),
											meta: {
												url: window.location.href,
											},
										});
									window.open(`${WEBSITE_URL}/vip-access`, '_blank');
								}}
							>
								Book A 15 min Demo
							</Button>
						) : null}
					</div>
				) : null}

				<div className="mb-5 text-xs md:w-[358px] text-center dark:text-[#C7C9D9] text-[#555770]">
					By signing up, I accept the LevelUp{' '}
					<a target="_blank" href={`https://levelup.masaischool.com/terms-and-conditions`} className="underline text-[#7777E7]">
						Terms of Service
					</a>{' '}
					and acknowledge the{' '}
					<a target="_blank" href={`https://levelup.masaischool.com/privacy-policy`} className="underline text-[#7777E7]">
						Privacy Policy
					</a>
					.
				</div>
			</form>
		</div>
	);
};

export default RegisterForm;
